import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { GetUserAppDevicesResponseData } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { isApiError } from '@/common/util';
import { useCompaniesTranslation } from '../hooks';

/**
 * A single app device. The generated client only exposes the union inline on
 * the response, so it is derived here rather than duplicated.
 */
export type AppDevice = GetUserAppDevicesResponseData['appDevices'][number];

export type FetchUserAppDevicesResult = {
    status: 'error' | 'success' | 'pending';
    appDevices: AppDevice[] | undefined;
    /** Why the read failed, ready to show. Empty while it has not. */
    error: string;
    /** False when retrying cannot help — the member is gone, or refused. */
    canRetry: boolean;
    refetch: () => void;
};

/** The phone handsets a user has signed in to the mobile app on. */
export function useFetchUserAppDevices(
    userId: string
): FetchUserAppDevicesResult {
    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const describeError = useCallback(
        (error: unknown): { message: string; canRetry: boolean } => {
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return {
                    message: t('shared.errors.member_load_not_found'),
                    canRetry: false
                };
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return {
                    message: t('shared.errors.app_devices_load'),
                    canRetry: false
                };
            }

            return {
                message: t('shared.errors.app_devices_load'),
                canRetry: true
            };
        },
        [t]
    );

    const {
        status,
        data: appDevices,
        refetch,
        error
    } = useQuery({
        queryKey: ['fetchUserAppDevices', userId],
        queryFn: () => {
            return openApiRef.current.users
                .getUserAppDevices(userId)
                .then((response) => response.data.data.appDevices);
        }
    });

    const described = error ? describeError(error) : null;

    return {
        status,
        appDevices,
        error: described?.message ?? '',
        canRetry: described?.canRetry ?? true,
        refetch: () => {
            void refetch();
        }
    };
}
